import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import * as React from "react";

import { cn } from "@/lib/cn";

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
    maxHeight?: number;
    /**
     * The element that actually scrolls. The forwarded ref goes to the root,
     * which does not — so anything that wants to read or set scrollTop needs
     * this one.
     */
    viewportRef?: React.Ref<HTMLDivElement>;
    /**
     * Size the viewport by the flex algorithm instead of `height: 100%`.
     *
     * Use this whenever the area is meant to fill the space a flex parent hands
     * it. The default `height: 100%` needs an ancestor with a *definite* height
     * to resolve against, and a `max-height`-bounded column — a dialog capped at
     * 90vh, say — is not one: the percentage falls back to `auto`, the viewport
     * grows to its content instead of to its parent, nothing overflows it, and
     * the area silently stops scrolling while the root clips what does not fit.
     * As a flex child there is no percentage to resolve.
     */
    fill?: boolean;
  }
>(({ className, children, maxHeight, viewportRef, fill, ...props }, ref) => (
  <ScrollAreaPrimitive.Root
    ref={ref}
    className={cn(
      "tw-relative tw-overflow-hidden",
      fill && "tw-flex tw-flex-col",
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.Viewport
      ref={viewportRef}
      className={cn(
        "tw-w-full tw-overscroll-contain tw-rounded-[inherit] [&>div]:!tw-grid",
        fill ? "tw-min-h-0 tw-flex-1" : "tw-size-full",
      )}
      style={{ maxHeight: maxHeight ? `${maxHeight}px` : "inherit" }}
    >
      {children}
    </ScrollAreaPrimitive.Viewport>
    <ScrollBar />
    <ScrollAreaPrimitive.Corner />
  </ScrollAreaPrimitive.Root>
));
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "tw-z-[3] tw-flex tw-touch-none tw-select-none !tw-py-1 tw-transition-colors",
      orientation === "vertical" &&
        "tw-h-full tw-w-2.5 tw-border-l tw-border-l-transparent tw-p-[1px]",
      orientation === "horizontal" &&
        "tw-h-2.5 tw-flex-col tw-border-t tw-border-t-transparent tw-p-[1px]",
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="tw-bg-border tw-relative tw-max-w-1 tw-flex-1 tw-rounded-full tw-bg-[#b8b3c4]" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
